import * as React from "react";
import {
  CheckIcon,
  FilePlusIcon,
  FileTextIcon,
  GearSixIcon,
  GlobeHemisphereWestIcon,
  RocketLaunchIcon,
  XIcon
} from "@phosphor-icons/react";
import { cn } from "cn";
import { Button } from "@libs/zapi-ui/ds/components/button";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger
} from "@libs/zapi-ui/ds/components/dialog";
import { Checkbox } from "@libs/zapi-ui/ds/components/checkbox";
import { Field, FieldDescription, FieldError, FieldLabel } from "@libs/zapi-ui/ds/components/field";
import { FileDropZone, useFileDrop } from "@libs/zapi-ui/ds/components/file-drop-zone";
import { Input } from "@libs/zapi-ui/ds/components/input";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@libs/zapi-ui/ds/components/sheet";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@libs/zapi-ui/ds/components/select";
import { Spinner } from "@libs/zapi-ui/ds/components/spinner";
import { Tooltip, TooltipContent, TooltipTrigger } from "@libs/zapi-ui/ds/components/tooltip";
import { displayLink, normalizeLink } from "@/zapi-frontend/shared/lib/link";

type SetupLabels = {
  launchAnalysis: string;
  launching: string;
  launchAnalysisBlocked: (reason: string) => string;
  launchReadyTitle: string;
  launchReadyDescription: string;
  launchUnfinishedTitle: string;
  launchUnfinishedDescription: string;
  reviewFrame: string;
  keepGoing: string;
  launchAnyway: string;
  analysisSettings: string;
  generationModel: string;
  skipAnalysis: string;
  skipAnalysisDescription: string;
  cancel: string;
  saveChanges: string;
  linkDialogTitle: string;
  linkLabel: string;
  linkPlaceholder: string;
  invalidLink: string;
  addLink: string;
  addFiles: string;
  addWebLink: string;
  read: string;
  reading: string;
  remove: string;
  removeSource: (name: string) => string;
  vault: string;
  dropFilesTitle: string;
  dropFilesDescription: string;
  close: string;
};

const defaultSetupLabels: SetupLabels = {
  launchAnalysis: "Launch Analysis",
  launching: "Launching…",
  launchAnalysisBlocked: (reason) => `Launch Analysis: ${reason}`,
  launchReadyTitle: "Ready to launch?",
  launchReadyDescription:
    "Your case frame shapes every result, so give it a quick look before you start. The analysis takes about 3 hours.",
  launchUnfinishedTitle: "Launch analysis now?",
  launchUnfinishedDescription:
    "Your case frame isn’t finished, so the results may be less accurate. The analysis takes about 3 hours.",
  reviewFrame: "Review Frame",
  keepGoing: "Keep Going",
  launchAnyway: "Launch Anyway",
  analysisSettings: "Analysis settings",
  generationModel: "Generation model",
  skipAnalysis: "Skip analysis generation",
  skipAnalysisDescription: "Documents are still parsed when the analysis is skipped.",
  cancel: "Cancel",
  saveChanges: "Save Changes",
  linkDialogTitle: "Add a web link",
  linkLabel: "Link",
  linkPlaceholder: "https://",
  invalidLink: "That doesn’t look like a link.",
  addLink: "Add Link",
  addFiles: "Add Files",
  addWebLink: "Add Web Link",
  read: "Read",
  reading: "Reading",
  remove: "Remove",
  removeSource: (name) => `Remove ${name}`,
  vault: "Vault",
  dropFilesTitle: "Drop files here",
  dropFilesDescription: "PDF, Word, PowerPoint or Excel, or a web link.",
  close: "Close"
};

function SetupPanel({
  title,
  tools,
  action,
  className,
  children,
  ...props
}: Omit<React.ComponentProps<"section">, "title"> & {
  title: React.ReactNode;
  tools?: React.ReactNode;
  action: React.ReactNode;
}) {
  return (
    <section data-slot="setup-panel" className={cn("flex min-h-0 flex-col gap-5", className)} {...props}>
      <div className="flex flex-wrap items-center gap-x-6 gap-y-3 px-1 sm:flex-nowrap">
        <h1 className="min-w-0 flex-1 basis-full typo-h3 text-foreground sm:basis-auto">{title}</h1>
        <div className="ms-auto flex shrink-0 items-center gap-2">
          {tools}
          {action}
        </div>
      </div>
      <div className="-m-1 flex min-h-0 flex-1 flex-col overflow-y-auto p-1">{children}</div>
    </section>
  );
}

function SetupSurface({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="setup-surface"
      className={cn(
        "flex flex-1 flex-col rounded-2xl bg-card p-5 shadow-xs ring-1 ring-foreground/10 sm:p-8",
        className
      )}
      {...props}
    />
  );
}

function SetupLaunchButton({
  blockedReason,
  pending = false,
  onLaunch,
  labels
}: {
  blockedReason?: string;
  // The launch is under way: the button turns off and spins.
  pending?: boolean;
  onLaunch: () => void;
  labels?: Partial<SetupLabels>;
}) {
  const text = { ...defaultSetupLabels, ...labels };
  const button = (
    <Button size="lg" shape="pill" disabled={Boolean(blockedReason) || pending} onClick={onLaunch}>
      {pending ? (
        <Spinner data-icon="inline-start" aria-label={text.launching} />
      ) : (
        <RocketLaunchIcon data-icon="inline-start" />
      )}
      {pending ? text.launching : text.launchAnalysis}
    </Button>
  );
  if (!blockedReason) return button;
  // The disabled button takes no pointer events or focus, so a focusable wrapper shows why it is off.
  return (
    <Tooltip>
      <TooltipTrigger
        render={
          <span
            tabIndex={0}
            aria-label={text.launchAnalysisBlocked(blockedReason)}
            className="inline-flex cursor-not-allowed rounded-full outline-none focus-visible:ring-3 focus-visible:ring-ring/50"
          />
        }
      >
        {button}
      </TooltipTrigger>
      <TooltipContent side="bottom" align="end" className="max-w-52 text-balance">
        {blockedReason}
      </TooltipContent>
    </Tooltip>
  );
}

function SetupLaunchDialog({
  open,
  ready = false,
  onOpenChange,
  onLaunch,
  labels
}: {
  open: boolean;
  ready?: boolean;
  onOpenChange: (open: boolean) => void;
  onLaunch: () => void;
  labels?: Partial<SetupLabels>;
}) {
  const text = { ...defaultSetupLabels, ...labels };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent initialFocus={false} closeLabel={text.close} className="gap-5 rounded-2xl sm:max-w-md">
        <DialogHeader>
          <span className="mb-1 flex size-10 items-center justify-center rounded-xl bg-muted text-muted-foreground">
            <RocketLaunchIcon weight="duotone" className="size-5" />
          </span>
          <DialogTitle className="typo-h5">{ready ? text.launchReadyTitle : text.launchUnfinishedTitle}</DialogTitle>
          <DialogDescription className="typo-body-sm">
            {ready ? text.launchReadyDescription : text.launchUnfinishedDescription}
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          {/* eslint-disable-next-line jsx-a11y/no-autofocus */}
          <DialogClose render={<Button variant="outline" shape="pill" autoFocus />}>
            {ready ? text.reviewFrame : text.keepGoing}
          </DialogClose>
          <Button
            shape="pill"
            onClick={() => {
              onOpenChange(false);
              onLaunch();
            }}
          >
            <RocketLaunchIcon data-icon="inline-start" />
            {ready ? text.launchAnalysis : text.launchAnyway}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

type SetupAnalysisSettingsValue = { model: string; skipAnalysis: boolean };

function SetupAnalysisSettings({
  models,
  value,
  onSave,
  labels
}: {
  models: { value: string; label: string }[];
  value: SetupAnalysisSettingsValue;
  onSave: (value: SetupAnalysisSettingsValue) => void;
  labels?: Partial<SetupLabels>;
}) {
  const text = { ...defaultSetupLabels, ...labels };
  const [open, setOpen] = React.useState(false);
  const [draft, setDraft] = React.useState(value);
  const modelId = React.useId();
  const skipId = React.useId();

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (next) setDraft(value);
        setOpen(next);
      }}
    >
      <DialogTrigger
        render={<Button variant="outline" size="icon-lg" shape="pill" aria-label={text.analysisSettings} />}
      >
        <GearSixIcon />
      </DialogTrigger>
      <DialogContent initialFocus={false} closeLabel={text.close} className="gap-5 rounded-2xl sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="typo-h5">{text.analysisSettings}</DialogTitle>
        </DialogHeader>
        <Field>
          <FieldLabel htmlFor={modelId}>{text.generationModel}</FieldLabel>
          <Select
            items={models}
            value={draft.model}
            onValueChange={(next) => typeof next === "string" && setDraft((current) => ({ ...current, model: next }))}
          >
            <SelectTrigger id={modelId} className="w-full">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {models.map((model) => (
                <SelectItem key={model.value} value={model.value}>
                  {model.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Field>
        <Field orientation="horizontal" className="items-start">
          <Checkbox
            id={skipId}
            checked={draft.skipAnalysis}
            onCheckedChange={(checked) =>
              setDraft((current) => ({
                ...current,
                skipAnalysis: checked === true
              }))
            }
          />
          <div className="flex flex-col gap-1">
            <FieldLabel htmlFor={skipId}>{text.skipAnalysis}</FieldLabel>
            <FieldDescription>{text.skipAnalysisDescription}</FieldDescription>
          </div>
        </Field>
        <DialogFooter>
          <DialogClose render={<Button variant="outline" shape="pill" />}>{text.cancel}</DialogClose>
          <Button
            shape="pill"
            onClick={() => {
              onSave(draft);
              setOpen(false);
            }}
          >
            {text.saveChanges}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function SetupLinkDialog({
  open,
  onOpenChange,
  onAdd,
  labels
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onAdd: (url: string) => void;
  labels?: Partial<SetupLabels>;
}) {
  const text = { ...defaultSetupLabels, ...labels };
  const inputId = React.useId();
  const [value, setValue] = React.useState("");
  const [invalid, setInvalid] = React.useState(false);
  const close = (next: boolean) => {
    onOpenChange(next);
    if (!next) {
      setValue("");
      setInvalid(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent
        closeLabel={text.close}
        className="gap-5 rounded-2xl sm:max-w-md [&_[data-slot=dialog-close]]:top-7"
      >
        <DialogHeader className="flex-row items-center gap-3 pe-8">
          <span className="flex size-10 shrink-0 items-center justify-center rounded-xl bg-muted text-muted-foreground">
            <GlobeHemisphereWestIcon className="size-5" aria-hidden="true" />
          </span>
          <DialogTitle className="typo-h5">{text.linkDialogTitle}</DialogTitle>
        </DialogHeader>
        <form
          className="flex flex-col gap-5"
          onSubmit={(event) => {
            event.preventDefault();
            const url = normalizeLink(value);
            if (!url) return setInvalid(true);
            onAdd(url);
            close(false);
          }}
        >
          <Field data-invalid={invalid || undefined}>
            <FieldLabel htmlFor={inputId}>{text.linkLabel}</FieldLabel>
            <Input
              id={inputId}
              value={value}
              aria-invalid={invalid || undefined}
              placeholder={text.linkPlaceholder}
              // eslint-disable-next-line jsx-a11y/no-autofocus
              autoFocus
              onChange={(event) => {
                setValue(event.target.value);
                setInvalid(false);
              }}
            />
            {invalid && <FieldError>{text.invalidLink}</FieldError>}
          </Field>
          <DialogFooter>
            <Button type="button" variant="ghost" shape="pill" onClick={() => close(false)}>
              {text.cancel}
            </Button>
            <Button type="submit" shape="pill" disabled={!value.trim()}>
              <CheckIcon data-icon="inline-start" />
              {text.addLink}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

const SOURCE_ACTIONS = {
  files: { label: defaultSetupLabels.addFiles, icon: FilePlusIcon },
  link: { label: defaultSetupLabels.addWebLink, icon: GlobeHemisphereWestIcon }
} as const;

type SetupSource = {
  id: string;
  kind: "file" | "link";
  name: string;
  status: "reading" | "done";
  // False hides the remove button, e.g. while the source is still being added.
  removable?: boolean;
};

function SetupSourceList({
  sources,
  onRemove,
  onAddFiles,
  onAddLink,
  dragging = false,
  labels,
  className,
  ...props
}: React.ComponentProps<"div"> & {
  sources: SetupSource[];
  onRemove?: (sourceId: string) => void;
  onAddFiles?: (files: File[]) => void;
  onAddLink?: (url: string) => void;
  dragging?: boolean;
  labels?: Partial<SetupLabels>;
}) {
  const text = { ...defaultSetupLabels, ...labels };
  const input = React.useRef<HTMLInputElement>(null);
  const [linkOpen, setLinkOpen] = React.useState(false);

  return (
    <div
      data-slot="setup-source-list"
      data-dragging={dragging || undefined}
      className={cn(
        "group/sources flex min-h-0 flex-col overflow-hidden rounded-2xl bg-card shadow-xs ring-1 ring-foreground/10 transition-shadow data-dragging:ring-2 data-dragging:ring-primary",
        className
      )}
      {...props}
    >
      <ul className="flex min-h-0 flex-col divide-y overflow-y-auto">
        {sources.map((source) => {
          const SourceIcon = source.kind === "link" ? GlobeHemisphereWestIcon : FileTextIcon;
          return (
            <li
              key={source.id}
              data-status={source.status}
              className="flex animate-in items-center gap-4 px-5 py-4 fade-in"
            >
              <span className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-muted text-muted-foreground">
                <SourceIcon className="size-5" aria-hidden="true" />
              </span>
              <span className="min-w-0 flex-1 truncate typo-body-md text-foreground" title={source.name}>
                {source.kind === "link" ? displayLink(source.name) : source.name}
              </span>
              {source.status === "done" ? (
                <span
                  title={text.read}
                  className="flex shrink-0 animate-in items-center gap-1.5 typo-label-md text-success fade-in"
                >
                  <CheckIcon weight="bold" className="size-4" aria-hidden="true" />
                  <span className="sr-only">{text.read}</span>
                </span>
              ) : (
                <span
                  title={text.reading}
                  className="flex shrink-0 items-center gap-1.5 typo-label-md text-muted-foreground"
                >
                  <Spinner className="size-4" aria-label={text.reading} aria-hidden="true" />
                  <span className="sr-only">{text.reading}</span>
                </span>
              )}
              {onRemove && (
                <Button
                  variant="ghost"
                  size="icon-sm"
                  aria-label={text.removeSource(source.name)}
                  title={text.remove}
                  disabled={source.removable === false}
                  className={cn("-me-1.5 text-muted-foreground", source.removable === false && "invisible")}
                  onClick={() => onRemove(source.id)}
                >
                  <XIcon />
                </Button>
              )}
            </li>
          );
        })}
      </ul>
      {(onAddFiles || onAddLink) && (
        <div className="flex shrink-0 flex-col gap-2 border-t p-3 transition-colors group-data-dragging/sources:bg-primary/5">
          <div className="grid auto-cols-fr grid-flow-col gap-2">
            {onAddFiles && (
              <Button variant="outline" shape="pill" className="bg-background" onClick={() => input.current?.click()}>
                <FilePlusIcon data-icon="inline-start" />
                {text.addFiles}
              </Button>
            )}
            {onAddLink && (
              <Button variant="outline" shape="pill" className="bg-background" onClick={() => setLinkOpen(true)}>
                <GlobeHemisphereWestIcon data-icon="inline-start" />
                {text.addWebLink}
              </Button>
            )}
          </div>
        </div>
      )}
      {onAddFiles && (
        <input
          ref={input}
          type="file"
          multiple
          className="sr-only"
          tabIndex={-1}
          aria-hidden="true"
          onChange={(event) => {
            const selected = Array.from(event.target.files ?? []);
            event.target.value = "";
            if (selected.length > 0) onAddFiles(selected);
          }}
        />
      )}
      {onAddLink && <SetupLinkDialog open={linkOpen} onOpenChange={setLinkOpen} onAdd={onAddLink} labels={labels} />}
    </div>
  );
}

function SetupVaultSheet({
  open,
  onOpenChange,
  sources,
  onRemove,
  onAddFiles,
  onAddLink,
  labels
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  sources: SetupSource[];
  onRemove?: (sourceId: string) => void;
  onAddFiles?: (files: File[]) => void;
  onAddLink?: (url: string) => void;
  labels?: Partial<SetupLabels>;
}) {
  const text = { ...defaultSetupLabels, ...labels };
  const { dragging, dropProps } = useFileDrop(onAddFiles ?? (() => undefined));
  const [linkOpen, setLinkOpen] = React.useState(false);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        initialFocus={false}
        closeLabel={text.close}
        className="gap-0 bg-canvas p-0 data-[side=right]:w-full data-[side=right]:sm:max-w-md"
        {...(onAddFiles ? dropProps : {})}
      >
        <SheetHeader className="gap-1 px-6 pt-5 pb-4">
          <SheetTitle className="typo-h5">{text.vault}</SheetTitle>
        </SheetHeader>
        <div className="flex min-h-0 flex-1 flex-col gap-3 px-4 pb-4">
          {sources.length > 0 ? (
            <SetupSourceList
              sources={sources}
              onRemove={onRemove}
              onAddFiles={onAddFiles}
              onAddLink={onAddLink}
              dragging={dragging}
              labels={labels}
            />
          ) : (
            onAddFiles && (
              <FileDropZone
                title={text.dropFilesTitle}
                description={text.dropFilesDescription}
                actionLabel={text.addFiles}
                actionIcon={FilePlusIcon}
                actionVariant="outline"
                secondaryAction={
                  onAddLink && (
                    <Button variant="outline" size="lg" shape="pill" onClick={() => setLinkOpen(true)}>
                      <GlobeHemisphereWestIcon data-icon="inline-start" />
                      {text.addWebLink}
                    </Button>
                  )
                }
                onFiles={onAddFiles}
                className={cn("flex-1 bg-card backdrop-blur-none", dragging && "border-primary bg-primary/5")}
              />
            )
          )}
          {onAddLink && sources.length === 0 && (
            <SetupLinkDialog open={linkOpen} onOpenChange={setLinkOpen} onAdd={onAddLink} labels={labels} />
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}

export {
  defaultSetupLabels,
  SetupAnalysisSettings,
  SetupLaunchButton,
  SetupLaunchDialog,
  SetupLinkDialog,
  SetupPanel,
  SetupSourceList,
  SetupSurface,
  SetupVaultSheet,
  SOURCE_ACTIONS,
  type SetupAnalysisSettingsValue,
  type SetupLabels,
  type SetupSource
};
